import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { BrokerSliceKind, BrokerSliceVinRow, ClientViewParams, BrokerChipFilterParams } from '../../api/types';
import { siftApiClient } from '../../api/siftApiClient';
import { USER_STATUS_DISPLAY_DEFAULT, USER_STATUS_OPTIONS } from '../../types/sift';
import { statusSetterName } from './brokerVinStatus';
import {
  PRIORITY_COLOR, PRIORITY_SHORT_LABEL,
  PRICE_COLOR, PRICE_LABEL, PRICE_SHORT_LABEL,
  MATCH_TIER_LABEL, MATCH_TIER_COLOR, MATCH_TIER_DESC,
  vinSeverityScore, vinSeverityKey, vinSeverityTooltip, priorityKeyFromIndex,
  vinPriceRatio, vinPriceBucketIndex, priceKeyFromIndex,
  fmtCrGrade, isValidCrGrade, getBrokerThresholds, vinBundleFacts, vinLocationLabel,
} from './brokerViewTokens';
import './DealerVinTable.css';

/** The four buyer-match tiers a VIN's resolved match can fall into. */
export type MatchType = 'exact' | 'cohort' | 'similar' | 'makemodel' | 'related';

// How many of a VIN's ranked buyers one add can stage. The plain "+" stages the
// best buyer only; the caret menu opts into a wider set (Jordan, 8/5).
const BASKET_TOP_N_CHOICES = [1, 3, 5, 10];

interface BrokerVinTableProps {
  /** Which slice the parent card belongs to (drives the VIN↔card join). */
  slice: BrokerSliceKind;
  /** The slice key: seller_id / buyer_uid / cluster_key. */
  slotKey: string;
  /** Cross-cutting board filters (market center / seller name / auction). */
  boardParams?: Pick<ClientViewParams, 'market_center' | 'seller_name' | 'auction'>;
  /** Match-type filter (empty = all): "VIN has a buyer at this tier". */
  matchTypes?: MatchType[];
  /** Shared chip filters (urgency / days / listing / bids / price), applied server-side. */
  chipFilters?: BrokerChipFilterParams;
  /** Row click (opens the VIN detail pop-out). */
  onVinClick?: (v: BrokerSliceVinRow) => void;
  /**
   * Stage a VIN in the outreach basket under its top `topN` buyers (1 = best
   * buyer only). Column hidden when omitted.
   */
  onAddToBasket?: (v: BrokerSliceVinRow, topN: number) => void;
  /**
   * VINs already staged in the basket under ANY buyer. A row reads as added once
   * it is staged, since one click can stage it under several buyers.
   */
  basketVins?: Set<string>;
  /** Undo: clicking a staged row's ✓ removes that VIN from every buyer it was staged under. */
  onRemoveFromBasket?: (vin: string) => void;
  /**
   * Show the resolved-buyer column. Defaults to true for seller/cluster (each VIN
   * shows its BEST buyer) and false for the buyer slice (buyer is fixed).
   */
  showBuyer?: boolean;
  /**
   * Show the seller column. Defaults to true for buyer/cluster (VINs span sellers)
   * and false for the seller slice (redundant — it's the card's seller).
   */
  showSeller?: boolean;
}

type SortDir = 'asc' | 'desc';

interface VinColumn {
  key: string;
  label: string;
  /** Backend sort token (from the get_slice_vins allow-list); omit = not sortable. */
  sortKey?: string;
  numeric?: boolean;
  cellClass?: string;
  /** Column-header help text (defines the concept; distinct grains per §11.3). */
  help?: string;
  render: (v: BrokerSliceVinRow) => React.ReactNode;
}

const PAGE_SIZE = 50;

// §11.3 — the three concepts live at different grains; keep the definitions
// explicit in the header tooltips so users don't conflate them.
const URGENCY_HELP =
  'Urgency (per VIN) — should you work this VIN? Fixed-range severity from aging, listing occurrences, current bids, repricing, market signals, and price position. Critical / High / Medium / Low. Same on every tab. Not the fit score.';
const MATCH_HELP =
  'Match type (buyer ↔ VIN) — how tightly the resolved buyer fits this VIN: Exact (cohort + trim + drivetrain) · Cohort (age + mileage + CR) · Similar (age + mileage or CR) · M/M (make/model only). On Seller/Cluster it is the VIN\'s BEST available buyer; on Buyer it is that buyer.';
const FIT_HELP =
  'Fit (buyer ↔ VIN) — 0-100 blend of match tier + proximity + buyer strength + MMR-retention + recency + engagement. The "best match" signal that picks and orders the top-5. Not the VIN\'s urgency.';
const RDS_STATUS_HELP =
  'Where this VIN sits in the workflow. Unassigned = not yet picked up · In Progress = being worked · Updated = price/listing was refreshed · Reinspection Needed · Done · Sold / NA.';
const BROKER_STATUS_HELP =
  'The broker team\u2019s own workflow status for this VIN — tracked per broker group and independent of the read-only RDS status. Editable here; changes are scoped to your group.';

/** Resolve a VIN's urgency badge, preferring the server tier when present. */
function severityKeyFor(v: BrokerSliceVinRow) {
  return v.severity_tier != null
    ? priorityKeyFromIndex(v.severity_tier)
    : vinSeverityKey(vinSeverityScore(v));
}

// §8.4 — adverse-condition detection (salvage / structural / as-is / adverse
// announcement text) + the high-CR-with-adverse warning (a CR ≥ 4 VIN that still
// carries an adverse signal — the CAFS gap brokers must verify before outreach).
const ADVERSE_ANNC_RE = /salvage|structural|frame|as[\s-]?is|airbag|inop|flood|lemon|branded|deployed/i;
const condBadgeStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, padding: '0 5px', borderRadius: 8, whiteSpace: 'nowrap',
};

function vinAdverse(v: BrokerSliceVinRow) {
  const salvage = Number(v.salvage_flag) === 1;
  const frame = Number(v.frame_damage) === 1;
  const asIs = Number(v.as_is) === 1;
  const anncAdverse = !!(v.announcements && ADVERSE_ANNC_RE.test(v.announcements));
  const relist = Number(v.is_second_chance) === 1;
  const any = salvage || frame || asIs || anncAdverse || relist;
  const cr = isValidCrGrade(v.cr_grade) ? Number(v.cr_grade) : null;
  const highCrWarn = (salvage || frame || asIs || anncAdverse) && cr != null && cr >= 4.0;
  return { salvage, frame, asIs, anncAdverse, relist, any, highCrWarn };
}

/**
 * The unified §11 slice VIN table. One server-paginated table shared by the
 * Seller / Buyer / Cluster slices — sort, search, match-type filter, and paging
 * are all server-side (via `getBrokerViewSliceVins`), so the table behaves
 * identically no matter which card opened it. Each row carries the resolved
 * buyer match: the VIN's BEST buyer on Seller/Cluster, the focused buyer on the
 * Buyer tab.
 */
export const BrokerVinTable: React.FC<BrokerVinTableProps> = ({
  slice,
  slotKey,
  boardParams,
  matchTypes,
  onVinClick,
  onAddToBasket,
  basketVins,
  onRemoveFromBasket,
  showBuyer,
  showSeller,
  chipFilters,
}) => {
  const [rows, setRows] = useState<BrokerSliceVinRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState<string>('urgency');
  const [sortOrder, setSortOrder] = useState<SortDir>('desc');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedVins, setSelectedVins] = useState<Set<string>>(new Set());
  const [copiedVins, setCopiedVins] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  // Row whose "add for top N buyers" menu is open.
  const [basketMenuVin, setBasketMenuVin] = useState<string | null>(null);

  const includeBuyerCol = showBuyer ?? slice !== 'buyer';
  const includeSellerCol = showSeller ?? slice !== 'seller';
  const matchTypesCsv = useMemo(
    () => (matchTypes && matchTypes.length > 0 ? matchTypes.join(',') : undefined),
    [matchTypes],
  );
  const chipKey = useMemo(() => JSON.stringify(chipFilters ?? {}), [chipFilters]);

  // Debounce the free-text VIN search (server-side filter).
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Dismiss the top-N basket menu on any outside click.
  useEffect(() => {
    if (!basketMenuVin) return;
    const close = () => setBasketMenuVin(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [basketMenuVin]);

  // Reset to the first page whenever the query shape changes.
  useEffect(() => {
    setOffset(0);
  }, [slice, slotKey, boardParams?.market_center, boardParams?.seller_name, boardParams?.auction, matchTypesCsv, chipKey, search, sortBy, sortOrder]);

  const reqSeq = useRef(0);
  useEffect(() => {
    const seq = ++reqSeq.current;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await siftApiClient.getBrokerViewSliceVins(slice, slotKey, {
          market_center: boardParams?.market_center,
          seller_name: boardParams?.seller_name,
          auction: boardParams?.auction,
          vin: search || undefined,
          match_types: matchTypesCsv,
          ...(chipFilters ?? {}),
          sort_by: sortBy,
          sort_order: sortOrder,
          limit: PAGE_SIZE,
          offset,
        });
        if (cancelled || seq !== reqSeq.current) return;
        setRows(resp.vins);
        setTotal(resp.total);
      } catch (err: any) {
        if (cancelled || seq !== reqSeq.current) return;
        setError(err?.response?.data?.detail || 'Failed to load VINs');
      } finally {
        if (!cancelled && seq === reqSeq.current) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slice, slotKey, boardParams?.market_center, boardParams?.seller_name, boardParams?.auction, matchTypesCsv, chipKey, search, sortBy, sortOrder, offset]);

  // §10-D: set/clear the broker-group VIN status (independent of the RDS status).
  // Optimistically patches the row; reverts on error. The backend gates edits to
  // broker/admin, so a rejection surfaces as an inline error.
  const [statusError, setStatusError] = useState<string | null>(null);
  const [savingStatusVins, setSavingStatusVins] = useState<Set<string>>(new Set());
  const updateBrokerStatus = async (vin: string, next: string) => {
    const clean = (next || '').trim();
    setStatusError(null);
    setSavingStatusVins((prev) => new Set(prev).add(vin));
    // Snapshot the prior row so we can revert on failure.
    const prior = rows.find((r) => r.vin === vin);
    setRows((prev) => prev.map((r) => (r.vin === vin ? { ...r, broker_status: clean || null } : r)));
    try {
      if (clean) {
        await siftApiClient.setBrokerVinStatus(vin, clean);
      } else {
        await siftApiClient.clearBrokerVinStatus(vin);
      }
    } catch (err: any) {
      // Revert to the prior value.
      setRows((prev) => prev.map((r) => (r.vin === vin ? { ...r, broker_status: prior?.broker_status ?? null } : r)));
      setStatusError(err?.response?.data?.detail || 'Failed to update broker status');
    } finally {
      setSavingStatusVins((prev) => {
        const nextSet = new Set(prev);
        nextSet.delete(vin);
        return nextSet;
      });
    }
  };

  const columns = useMemo<VinColumn[]>(() => {
    const cols: VinColumn[] = [
      {
        key: 'severity',
        label: 'Urgency',
        sortKey: 'urgency',
        help: URGENCY_HELP,
        render: (v) => {
          const key = severityKeyFor(v);
          const color = PRIORITY_COLOR[key];
          return (
            <span className="bv-vin-sev-badge" style={{ background: color.bg, color: color.fg }} title={vinSeverityTooltip(v)}>
              {PRIORITY_SHORT_LABEL[key]}
            </span>
          );
        },
      },
      {
        key: 'user_status',
        label: 'RDS Status',
        help: RDS_STATUS_HELP,
        render: (v) => {
          const s = v.user_status || USER_STATUS_DISPLAY_DEFAULT;
          const by = statusSetterName(v.user_status_by);
          const when = v.user_status_at ? new Date(v.user_status_at).toLocaleDateString() : null;
          const title = (by && v.user_status)
            ? `${s} — set by ${by}${when ? ` on ${when}` : ''}`
            : s;
          return (
            <span title={title}>
              {s}
              {by && v.user_status && (
                <span className="bv-vin-muted" style={{ display: 'block', fontSize: '0.75em' }}>by {by}</span>
              )}
            </span>
          );
        },
      },
      {
        key: 'broker_status',
        label: 'Broker Status',
        help: BROKER_STATUS_HELP,
        render: (v) => {
          const saving = savingStatusVins.has(v.vin);
          const by = statusSetterName(v.broker_status_by);
          const when = v.broker_status_at ? new Date(v.broker_status_at).toLocaleDateString() : null;
          const setTitle = (by && v.broker_status)
            ? `${v.broker_status} — set by ${by}${when ? ` on ${when}` : ''}`
            : 'Set the broker-group status for this VIN';
          return (
            <div className="bv-vin-status-edit" title={setTitle}>
              <select
                className="bv-vin-status-select"
                value={v.broker_status || ''}
                disabled={saving}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => { e.stopPropagation(); void updateBrokerStatus(v.vin, e.target.value); }}
              >
                <option value="">{USER_STATUS_DISPLAY_DEFAULT}</option>
                {USER_STATUS_OPTIONS.map((s) => (
                  <option key={String(s)} value={String(s)}>{s}</option>
                ))}
              </select>
              {by && v.broker_status && (
                <span className="bv-vin-muted" style={{ display: 'block', fontSize: '0.75em' }}>by {by}</span>
              )}
            </div>
          );
        },
      },
      {
        key: 'vin',
        label: 'VIN',
        sortKey: 'vin',
        cellClass: 'bv-vin-cell',
        render: (v) => <span title={v.vin}>{v.vin}</span>,
      },
      {
        key: 'ymm',
        label: 'Year / Make / Model',
        sortKey: 'ymm',
        render: (v) => {
          const ymm = [v.model_year, v.make, v.model].filter(Boolean).join(' ') || '–';
          const dsr = v.days_since_reprice;
          const recent = dsr != null && dsr <= getBrokerThresholds().urgency.reprice_days;
          const makeOffer = Number(v.allow_make_offer) === 1;
          const rc = v.reprice_count;
          const loc = vinLocationLabel(v);
          return (
            <span title={ymm}>
              {ymm}
              {makeOffer && (
                <span
                  title="Make-offer listing — retractable offers (~90% of asking), so more buyers are viable"
                  style={{ ...condBadgeStyle, marginLeft: 6, background: '#dbeafe', color: '#1e40af' }}
                >Offer</span>
              )}
              {recent && (
                <span
                  title={`Repriced ${dsr}d ago — the price / listing was just moved`}
                  style={{ ...condBadgeStyle, marginLeft: 6, background: '#dcfce7', color: '#166534' }}
                >↻ {dsr}d</span>
              )}
              {rc === 0 && (
                <span
                  title="No price adjustments — this unit's floor has never been repriced. A stuck unit that hasn't been worked on price."
                  style={{ ...condBadgeStyle, marginLeft: 6, background: '#fee2e2', color: '#991b1b' }}
                >No reprice</span>
              )}
              {rc != null && rc > 0 && !recent && (
                <span
                  title={`Repriced ${rc}\u00d7 — the floor has been adjusted ${rc} time${rc === 1 ? '' : 's'} but the unit hasn't sold`}
                  style={{ ...condBadgeStyle, marginLeft: 6, background: '#e0e7ff', color: '#3730a3' }}
                >↻ {rc}×</span>
              )}
              <span className="bv-vin-bundle" title="Miles · CR grade · adjusted MMR">{vinBundleFacts(v)}</span>
              {loc && <span className="bv-vin-place" title={loc.title}>📍 {loc.text}</span>}
            </span>
          );
        },
      },
      {
        key: 'match',
        label: 'Match',
        sortKey: 'match',
        help: MATCH_HELP,
        render: (v) => {
          if (v.match_tier == null) return <span className="bv-vin-muted">–</span>;
          const color = MATCH_TIER_COLOR[v.match_tier];
          return (
            <span
              className="bv-vin-match-badge"
              style={color ? { background: color.bg, color: color.fg } : undefined}
              title={MATCH_TIER_DESC[v.match_tier]}
            >
              {MATCH_TIER_LABEL[v.match_tier] ?? '–'}
            </span>
          );
        },
      },
      {
        key: 'fit',
        label: 'Fit',
        sortKey: 'fit',
        help: FIT_HELP,
        numeric: true,
        render: (v) => {
          if (v.fit_score == null) return <span className="bv-vin-muted">–</span>;
          const label = v.match_tier != null ? MATCH_TIER_LABEL[v.match_tier] ?? 'Make/Model' : 'Make/Model';
          return (
            <span className="bv-vin-fit" title={`Fit ${Math.round(v.fit_score)}/100 · Match: ${label}`}>
              {Math.round(v.fit_score)}
            </span>
          );
        },
      },
      {
        key: 'cr_grade',
        label: 'CR',
        sortKey: 'cr',
        numeric: true,
        render: (v) => fmtCrGrade(v.cr_grade),
      },
      {
        key: 'condition',
        label: 'Cond',
        help: 'Adverse-condition flags: Salvage · Frame/structural · As-Is · adverse announcement · 2nd-chance relist. ⚠ CR marks a high-CR (≥4.0) VIN that still carries an adverse signal — verify announcements before outreach.',
        render: (v) => {
          const a = vinAdverse(v);
          if (!a.any) return <span className="bv-vin-muted">–</span>;
          return (
            <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
              {a.highCrWarn && (
                <span title="High CR (≥4.0) but carries an adverse condition/announcement — verify before outreach"
                  style={{ ...condBadgeStyle, background: '#fde68a', color: '#854d0e' }}>⚠ CR</span>
              )}
              {a.salvage && (
                <span title="Salvage title" style={{ ...condBadgeStyle, background: '#fee2e2', color: '#b91c1c' }}>Salv</span>
              )}
              {a.frame && (
                <span title="Frame / structural damage" style={{ ...condBadgeStyle, background: '#fee2e2', color: '#b91c1c' }}>Frame</span>
              )}
              {a.asIs && (
                <span title="Sold as-is" style={{ ...condBadgeStyle, background: '#ffedd5', color: '#9a3412' }}>As-Is</span>
              )}
              {!a.salvage && !a.frame && !a.asIs && a.anncAdverse && (
                <span title="Adverse announcement text" style={{ ...condBadgeStyle, background: '#e0e7ff', color: '#3730a3' }}>Annc</span>
              )}
              {a.relist && (
                <span title="2nd-chance relist — a digital relist of a prior unsold live/simulcast listing" style={{ ...condBadgeStyle, background: '#ede9fe', color: '#6d28d9' }}>Relist</span>
              )}
            </span>
          );
        },
      },
      {
        key: 'price',
        label: 'Price',
        sortKey: 'price',
        render: (v) => {
          const ratio = vinPriceRatio(v);
          const key = priceKeyFromIndex(vinPriceBucketIndex(ratio));
          if (!key || ratio == null) return '–';
          const color = PRICE_COLOR[key];
          return (
            <span className="bv-vin-price-badge" style={{ background: color.bg, color: color.fg }} title={`${PRICE_LABEL[key]} — asking ${Math.round(ratio * 100)}% of MMR`}>
              {PRICE_SHORT_LABEL[key]}
            </span>
          );
        },
      },
      {
        key: 'list_days',
        label: 'List d',
        sortKey: 'list_days',
        numeric: true,
        render: (v) => v.list_days ?? '–',
      },
      {
        key: 'listing_occurences',
        label: 'List O',
        sortKey: 'list_o',
        numeric: true,
        render: (v) => v.listing_occurences ?? '–',
      },
      {
        key: 'total_bids',
        label: 'Bids',
        sortKey: 'bids',
        numeric: true,
        render: (v) => v.total_bids ?? '–',
      },
      {
        key: 'engaged',
        label: 'Engaged',
        sortKey: 'engaged',
        numeric: true,
        help: 'Observed engagement on THIS exact VIN: real buyers who bid, viewed, or saved it (vs cohort activity). Shows total engaged buyers · actual bidders. Use the Engaged filter to keep only VINs with real interest.',
        render: (v) => {
          const eng = Number(v.engaged_buyers ?? 0);
          const bidders = Number(v.actual_bidders ?? 0);
          if (eng <= 0) return <span className="bv-vin-muted">–</span>;
          return (
            <span title={`${eng} buyer${eng === 1 ? '' : 's'} engaged this VIN${bidders > 0 ? ` · ${bidders} actually bid` : ' (viewed/saved)'}`}>
              {eng}
              {bidders > 0 && (
                <span style={{ marginLeft: 4, color: '#047857', fontWeight: 600 }}>
                  · {bidders}▲
                </span>
              )}
            </span>
          );
        },
      },
    ];
    if (includeBuyerCol) {
      // Best buyer for the VIN (Seller/Cluster). Sits next to Match/Fit context.
      cols.splice(6, 0, {
        key: 'buyer',
        label: 'Best Buyer',
        render: (v) => v.buyer_name || <span className="bv-vin-muted">–</span>,
      });
    }
    if (includeSellerCol) {
      // Seller identity (Buyer/Cluster tabs span sellers). Sits right after YMM.
      cols.splice(4, 0, {
        key: 'seller',
        label: 'Seller',
        render: (v) => (
          v.seller_name || v.seller_id
            ? <span title={[v.seller_id ? `#${v.seller_id}` : null, v.territory_state, v.seller_zip_code].filter(Boolean).join(' · ')}>{v.seller_name || `#${v.seller_id}`}</span>
            : <span className="bv-vin-muted">–</span>
        ),
      });
    }
    return cols;
  }, [includeBuyerCol, includeSellerCol, savingStatusVins, updateBrokerStatus]);

  const handleSortClick = (col: VinColumn) => {
    if (!col.sortKey) return;
    setSortBy((prevKey) => {
      if (prevKey === col.sortKey) {
        setSortOrder((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prevKey;
      }
      setSortOrder('desc');
      return col.sortKey!;
    });
  };

  const toggleVinSelected = (vin: string) => {
    setSelectedVins((prev) => {
      const next = new Set(prev);
      if (next.has(vin)) next.delete(vin); else next.add(vin);
      return next;
    });
  };

  const handleCopyVins = async () => {
    const vinList = Array.from(selectedVins);
    if (vinList.length === 0) return;
    try {
      await navigator.clipboard.writeText(vinList.join('\n'));
      setCopiedVins(true);
      setTimeout(() => setCopiedVins(false), 1500);
    } catch (err) {
      console.error('Failed to copy VINs to clipboard', err);
    }
  };

  // §10-D bulk broker-status assign: optimistically patch every selected VIN's
  // broker_status, then persist via the bulk route (scoped to the caller's group);
  // revert all on failure. Mirrors the Client-View / main-table bulk assign.
  const handleBulkStatusAssign = async (next: string | null) => {
    const vinList = Array.from(selectedVins);
    if (vinList.length === 0) {
      setShowBulkStatusModal(false);
      return;
    }
    const clean = (next || '').trim();
    const targetSet = new Set(vinList);
    const prevRows = rows;
    setStatusError(null);
    setBulkAssigning(true);
    setRows((prev) => prev.map((r) => (targetSet.has(r.vin) ? { ...r, broker_status: clean || null } : r)));
    try {
      if (clean) {
        await siftApiClient.setBrokerVinStatusBulk(vinList, clean);
      } else {
        await siftApiClient.clearBrokerVinStatusBulk(vinList);
      }
      setSelectedVins(new Set());
      setShowBulkStatusModal(false);
    } catch (err: any) {
      setRows(prevRows);
      setStatusError(err?.response?.data?.detail || 'Failed to bulk update broker status');
    } finally {
      setBulkAssigning(false);
    }
  };

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + rows.length, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;
  const colCount = columns.length + 1 + (onAddToBasket ? 1 : 0);

  return (
    <div className="bv-vin-table-panel">
      <div className="bv-vins-header">
        <h3>
          VINs ({total})
          {selectedVins.size > 0 && <span className="bv-vins-sel-count"> — {selectedVins.size} selected</span>}
        </h3>
        <div className="bv-vins-controls">
          {selectedVins.size > 0 && (
            <>
              <button type="button" className="bv-vins-bulk-btn" onClick={() => setShowBulkStatusModal(true)}>
                Assign Status ({selectedVins.size})
              </button>
              <button type="button" className="bv-vins-bulk-btn" onClick={handleCopyVins}>
                {copiedVins ? 'Copied!' : `Copy VINs (${selectedVins.size})`}
              </button>
              <button type="button" className="bv-vins-bulk-btn bv-vins-bulk-btn-secondary" onClick={() => setSelectedVins(new Set())}>
                Clear
              </button>
            </>
          )}
          <input
            type="text"
            placeholder="Search VIN"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="bv-vin-search"
          />
        </div>
      </div>

      {error && <div className="bv-vin-table-error">{error}</div>}
      {statusError && <div className="bv-vin-table-error">{statusError}</div>}

      <div className="bv-vins-table-wrap">
        <table className="bv-vins-table">
          <thead>
            <tr>
              <th className="bv-vins-th-check">
                <input
                  type="checkbox"
                  aria-label="Select all visible VINs"
                  checked={rows.length > 0 && rows.every((v) => selectedVins.has(v.vin))}
                  ref={(el) => {
                    if (el) {
                      const c = rows.filter((v) => selectedVins.has(v.vin)).length;
                      el.indeterminate = c > 0 && c < rows.length;
                    }
                  }}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSelectedVins((prev) => {
                      const next = new Set(prev);
                      if (checked) rows.forEach((v) => next.add(v.vin));
                      else rows.forEach((v) => next.delete(v.vin));
                      return next;
                    });
                  }}
                />
              </th>
              {columns.map((col) => {
                const sorted = col.sortKey && sortBy === col.sortKey;
                const arrow = sorted ? (sortOrder === 'asc' ? '▲' : '▼') : '';
                return (
                  <th
                    key={col.key}
                    className={`bv-vins-th${sorted ? ' bv-vins-th-sorted' : ''}${col.numeric ? ' bv-vins-th-num' : ''}${col.sortKey ? '' : ' bv-vins-th-static'}`}
                    onClick={() => handleSortClick(col)}
                    title={
                      col.help
                        ? col.sortKey ? `${col.help}\n\nClick to sort.` : col.help
                        : col.sortKey ? 'Click to sort' : undefined
                    }
                  >
                    <span className="bv-vins-th-label">{col.label}</span>
                    {sorted && <span className="bv-vins-th-sort">{arrow}</span>}
                  </th>
                );
              })}
              {onAddToBasket && <th className="bv-vins-th bv-vins-th-static" />}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colCount} className="bv-vins-empty">Loading VINs…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={colCount} className="bv-vins-empty">No VINs match</td></tr>
            ) : (
              rows.map((v) => {
                const inBasket = basketVins?.has(v.vin) ?? false;
                return (
                  <tr key={v.vin} className="bv-vin-row" onClick={() => onVinClick?.(v)}
                    data-usage-vin={v.vin} data-usage-seller-uid={v.seller_id ?? undefined} data-usage-section="vin_table">
                    <td className="bv-vins-td-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select VIN ${v.vin}`}
                        checked={selectedVins.has(v.vin)}
                        onChange={() => toggleVinSelected(v.vin)}
                      />
                    </td>
                    {columns.map((col) => (
                      <td key={col.key} className={col.cellClass}>{col.render(v)}</td>
                    ))}
                    {onAddToBasket && (
                      <td className="bv-vins-td-basket" onClick={(e) => e.stopPropagation()}>
                        <div className="bv-vin-basket-wrap">
                          <button
                            type="button"
                            className={`bv-vin-basket-btn${inBasket ? ' bv-vin-basket-btn-added' : ''}`}
                            aria-pressed={inBasket}
                            disabled={inBasket ? !onRemoveFromBasket : !v.buyer_uid}
                            onClick={() => (inBasket ? onRemoveFromBasket?.(v.vin) : onAddToBasket(v, 1))}
                            title={
                              inBasket
                                ? (onRemoveFromBasket ? 'In basket — click to remove' : 'Already in basket')
                                : !v.buyer_uid
                                  ? 'No matched buyer to stage this VIN under'
                                  : `Add to basket (buyer: ${v.buyer_name || v.buyer_uid})`
                            }
                          >
                            {inBasket ? '✓' : '+'}
                          </button>
                          {/* Buyer slice is already scoped to one contact, so a top-N add is meaningless there. */}
                          {slice !== 'buyer' && !inBasket && v.buyer_uid && (
                            <button
                              type="button"
                              className="bv-vin-basket-more"
                              aria-haspopup="menu"
                              aria-expanded={basketMenuVin === v.vin}
                              onClick={() => setBasketMenuVin((cur) => (cur === v.vin ? null : v.vin))}
                              title="Add this VIN for more than its best buyer"
                            >
                              ▾
                            </button>
                          )}
                          {basketMenuVin === v.vin && (
                            <div className="bv-vin-basket-menu" role="menu">
                              {BASKET_TOP_N_CHOICES.map((n) => (
                                <button
                                  key={n}
                                  type="button"
                                  role="menuitem"
                                  className="bv-vin-basket-menu-item"
                                  onClick={() => {
                                    setBasketMenuVin(null);
                                    onAddToBasket(v, n);
                                  }}
                                >
                                  {n === 1 ? 'Best buyer only' : `Top ${n} buyers`}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="bv-vins-pager">
          <button type="button" className="bv-vins-pager-btn" disabled={!canPrev || loading} onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}>
            ‹ Prev
          </button>
          <span className="bv-vins-pager-info">{pageStart}–{pageEnd} of {total}</span>
          <button type="button" className="bv-vins-pager-btn" disabled={!canNext || loading} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
            Next ›
          </button>
        </div>
      )}

      {showBulkStatusModal && (
        <div className="bv-status-modal-overlay" onClick={() => setShowBulkStatusModal(false)}>
          <div className="bv-status-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bv-status-modal-header">
              <h3>Assign Broker Status</h3>
              <button className="bv-status-modal-close" aria-label="Close" onClick={() => setShowBulkStatusModal(false)}>×</button>
            </div>
            <div className="bv-status-modal-body">
              <p>Set the broker-group status for <strong>{selectedVins.size}</strong> selected VIN{selectedVins.size > 1 ? 's' : ''}:</p>
              <div className="bv-status-options">
                {USER_STATUS_OPTIONS.map((status) => (
                  <button
                    key={String(status)}
                    type="button"
                    className="bv-status-option"
                    disabled={bulkAssigning}
                    onClick={() => void handleBulkStatusAssign(String(status))}
                  >
                    {status}
                  </button>
                ))}
                <button
                  type="button"
                  className="bv-status-option bv-status-option-clear"
                  disabled={bulkAssigning}
                  onClick={() => void handleBulkStatusAssign(null)}
                >
                  {USER_STATUS_DISPLAY_DEFAULT}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
